import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Card,
  CardContent,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Payment,
  AttachMoney,
  Home,
  Person,
  Business,
  CheckCircle,
  Receipt,
  AccountBalance,
  Calculate,
  Warning,
} from '@mui/icons-material';

const InstallmentPayment = () => {
  const canWriteInstallment = hasWritePermission('plot-booking.installment-payment');
  const [bookingId, setBookingId] = useState('');
  const [showSearchForm, setShowSearchForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [bookingIdLoading, setBookingIdLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const [searchForm, setSearchForm] = useState({
    site: '',
    sector: '',
    block: '',
    plotNo: '',
  });

  const [paymentForm, setPaymentForm] = useState({
    receiptNo: '',
    paymentDate: '',
    paymentAmount: '',
    chequeBouncingCharges: '',
    lateFee: '',
    totalPayable: '',
    payoutProceed: 'not-proceed',
    paymentMode: '',
    receivedBy: '',
    givenBy: '',
    narration: '',
  });

  // Sample dropdown options
  const siteOptions = ['Green Valley', 'Hill View', 'Paradise Heights', 'Royal Gardens'];
  const sectorOptions = ['Sector A', 'Sector B', 'Sector C', 'Sector D'];
  const blockOptions = ['Block 1', 'Block 2', 'Block 3', 'Block 4'];
  const paymentModeOptions = [
    'Cash',
    'Cheque',
    'NEFT/RTGS',
    'Online Transfer',
    'UPI',
    'Credit Card',
    'Debit Card',
  ];

  const handleBookingIdSearch = async () => {
    if (!bookingId.trim()) {
      setErrors({ bookingId: 'Please enter a Booking ID' });
      return;
    }

    setBookingIdLoading(true);
    setErrors({});

    // Simulate API call
    setTimeout(() => {
      setShowSearchForm(true);
      setSuccessMessage('');
      console.log('Booking ID entered:', bookingId);
      setBookingIdLoading(false);
    }, 1500);
  };

  const handleSearchInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleSearch = async () => {
    setSearchLoading(true);

    // Simulate API call
    setTimeout(() => {
      setShowPaymentForm(true);
      console.log('Search criteria:', searchForm);

      // Auto-calculate total payable when form opens
      calculateTotalPayable();
      setSearchLoading(false);
    }, 1500);
  };

  const handlePaymentInputChange = (field, value) => {
    setPaymentForm({
      ...paymentForm,
      [field]: value,
    });

    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }

    // Auto-calculate total payable when payment amount, charges, or late fee changes
    if (field === 'paymentAmount' || field === 'chequeBouncingCharges' || field === 'lateFee') {
      const newForm = { ...paymentForm, [field]: value };
      const payment = parseFloat(newForm.paymentAmount) || 0;
      const charges = parseFloat(newForm.chequeBouncingCharges) || 0;
      const late = parseFloat(newForm.lateFee) || 0;
      const total = payment + charges + late;

      setPaymentForm({
        ...newForm,
        totalPayable: total.toString(),
      });
    }
  };

  const handleOpenPaymentDialog = () => {
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setErrors({});
  };

  const validatePaymentForm = () => {
    const newErrors = {};

    if (!paymentForm.paymentAmount.trim()) {
      newErrors.paymentAmount = 'Payment amount is required';
    }

    if (!paymentForm.paymentMode.trim()) {
      newErrors.paymentMode = 'Payment mode is required';
    }

    if (!paymentForm.paymentDate.trim()) {
      newErrors.paymentDate = 'Payment date is required';
    }

    return newErrors;
  };

  const calculateTotalPayable = () => {
    const payment = parseFloat(paymentForm.paymentAmount) || 0;
    const charges = parseFloat(paymentForm.chequeBouncingCharges) || 0;
    const late = parseFloat(paymentForm.lateFee) || 0;
    const total = payment + charges + late;

    setPaymentForm(prev => ({
      ...prev,
      totalPayable: total.toString(),
    }));
  };

  const handleSubmitPayment = async () => {
    const validationErrors = validatePaymentForm();

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitLoading(true);

    // Simulate API call
    setTimeout(() => {
      setSuccessMessage(`Installment payment of ₹${paymentForm.paymentAmount} has been submitted successfully for Booking ID: ${bookingId}`);
      setPaymentDialogOpen(false);
      setShowPaymentForm(false);
      setShowSearchForm(false);
      setBookingId('');
      // Reset payment form
      setPaymentForm({
        receiptNo: '',
        paymentDate: '',
        paymentAmount: '',
        chequeBouncingCharges: '',
        lateFee: '',
        totalPayable: '',
        payoutProceed: 'not-proceed',
        paymentMode: '',
        receivedBy: '',
        givenBy: '',
        narration: '',
      });
      setErrors({});
      console.log('Payment form data:', paymentForm);
      setSubmitLoading(false);
    }, 2000);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Installment Payment Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Process and manage installment payments for plot bookings
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Booking ID Input */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Enter Booking ID
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Booking ID"
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                placeholder="Enter Booking ID"
                error={!!errors.bookingId}
                helperText={errors.bookingId}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleBookingIdSearch}
                disabled={bookingIdLoading || !bookingId.trim()}
                sx={{
                  height: 56,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                  borderRadius: 2
                }}
              >
                {bookingIdLoading ? 'Processing...' : 'Enter'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Search Form */}
      {showSearchForm && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <Search sx={{ color: '#1976d2', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Plot Search Details
              </Typography>
            </Box>

            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Booking ID: {bookingId}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Select Site</InputLabel>
                  <Select
                    value={searchForm.site}
                    label="Select Site"
                    onChange={(e) => handleSearchInputChange('site', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Select Site</MenuItem>
                    {siteOptions.map((site) => (
                      <MenuItem key={site} value={site}>
                        {site}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Select Sector</InputLabel>
                  <Select
                    value={searchForm.sector}
                    label="Select Sector"
                    onChange={(e) => handleSearchInputChange('sector', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Select Sector</MenuItem>
                    {sectorOptions.map((sector) => (
                      <MenuItem key={sector} value={sector}>
                        {sector}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Select Block</InputLabel>
                  <Select
                    value={searchForm.block}
                    label="Select Block"
                    onChange={(e) => handleSearchInputChange('block', e.target.value)}
                    sx={{ borderRadius: 2 }}
                  >
                    <MenuItem value="">Select Block</MenuItem>
                    {blockOptions.map((block) => (
                      <MenuItem key={block} value={block}>
                        {block}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Plot No"
                  value={searchForm.plotNo}
                  onChange={(e) => handleSearchInputChange('plotNo', e.target.value)}
                  placeholder="Enter Plot Number"
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Home color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearch}
                    disabled={searchLoading}
                    startIcon={<Search />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '15px',
                      boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                      borderRadius: 2
                    }}
                  >
                    {searchLoading ? 'Searching...' : 'Search Plot Details'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Payment Form */}
      {showPaymentForm && (
        <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" gap={2} mb={3}>
              <AttachMoney sx={{ color: '#f57c00', fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                Installment Payment Details
              </Typography>
            </Box>

            <Typography variant="body2" color="primary" sx={{ mb: 3, fontWeight: 600 }}>
              Plot/Booking Details for Installment Payment Processing
            </Typography>

            {/* Payment Summary Cards */}
            {/* <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <AttachMoney sx={{ color: '#2e7d32', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Payment Amount</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                        ₹{paymentForm.paymentAmount || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Warning sx={{ color: '#f57c00', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Additional Charges</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
                        ₹{(parseFloat(paymentForm.chequeBouncingCharges || 0) + parseFloat(paymentForm.lateFee || 0)).toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card sx={{ p: 2, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Calculate sx={{ color: '#1976d2', fontSize: 24 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">Total Payable</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                        ₹{paymentForm.totalPayable || '0'}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            </Grid> */}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              {/* {canWriteInstallment && ( */}
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleOpenPaymentDialog}
                startIcon={<Payment />}
                sx={{
                  px: 6,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  borderRadius: 2
                }}
              >
                Process Installment Payment
              </Button>
              {/* )} */}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Payment sx={{ color: '#1976d2', fontSize: 28 }} />
            Installment Payment Form
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Receipt No"
                variant="outlined"
                fullWidth
                value={paymentForm.receiptNo}
                onChange={(e) => handlePaymentInputChange('receiptNo', e.target.value)}
                placeholder="Enter receipt number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Date *"
                type="date"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentDate}
                onChange={(e) => handlePaymentInputChange('paymentDate', e.target.value)}
                error={!!errors.paymentDate}
                helperText={errors.paymentDate}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Payment Amount *"
                variant="outlined"
                fullWidth
                value={paymentForm.paymentAmount}
                onChange={(e) => handlePaymentInputChange('paymentAmount', e.target.value)}
                placeholder="Enter payment amount"
                error={!!errors.paymentAmount}
                helperText={errors.paymentAmount || "Enter the installment payment amount"}
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Cheque Bouncing Charges"
                variant="outlined"
                fullWidth
                value={paymentForm.chequeBouncingCharges}
                onChange={(e) => handlePaymentInputChange('chequeBouncingCharges', e.target.value)}
                placeholder="Enter bouncing charges"
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Warning color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Late Fee"
                variant="outlined"
                fullWidth
                value={paymentForm.lateFee}
                onChange={(e) => handlePaymentInputChange('lateFee', e.target.value)}
                placeholder="Enter late fee"
                type="number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Warning color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Total Payable"
                variant="outlined"
                fullWidth
                value={paymentForm.totalPayable}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Calculate color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputBase-input': {
                    fontWeight: 'bold',
                    color: '#1976d2',
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
                  Payout Proceed
                </FormLabel>
                <RadioGroup
                  value={paymentForm.payoutProceed}
                  onChange={(e) => handlePaymentInputChange('payoutProceed', e.target.value)}
                  row
                >
                  <FormControlLabel
                    value="proceed"
                    control={<Radio />}
                    label="Payout Proceed"
                    sx={{ fontWeight: 600 }}
                  />
                  <FormControlLabel
                    value="not-proceed"
                    control={<Radio />}
                    label="Payout Not Proceed"
                    sx={{ fontWeight: 600 }}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.paymentMode}>
                <InputLabel>Payment Mode *</InputLabel>
                <Select
                  value={paymentForm.paymentMode}
                  label="Payment Mode *"
                  onChange={(e) => handlePaymentInputChange('paymentMode', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">Select Payment Mode</MenuItem>
                  {paymentModeOptions.map((mode) => (
                    <MenuItem key={mode} value={mode}>
                      {mode}
                    </MenuItem>
                  ))}
                </Select>
                {errors.paymentMode && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.paymentMode}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Received By"
                variant="outlined"
                fullWidth
                value={paymentForm.receivedBy}
                onChange={(e) => handlePaymentInputChange('receivedBy', e.target.value)}
                placeholder="Enter receiver name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Given By"
                variant="outlined"
                fullWidth
                value={paymentForm.givenBy}
                onChange={(e) => handlePaymentInputChange('givenBy', e.target.value)}
                placeholder="Enter giver name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Narration"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={paymentForm.narration}
                onChange={(e) => handlePaymentInputChange('narration', e.target.value)}
                placeholder="Enter payment description/notes..."
                helperText="Additional remarks or notes about the payment"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Cancel
          </Button>
          {/* {canWriteInstallment && ( */}
          <Button
            onClick={handleSubmitPayment}
            color="primary"
            variant="contained"
            disabled={!paymentForm.paymentAmount || !paymentForm.paymentMode || !paymentForm.paymentDate || submitLoading}
            startIcon={<Payment />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)'
            }}
          >
            {submitLoading ? 'Processing...' : 'Submit Payment'}
          </Button>
          {/* )} */}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallmentPayment;